import React from 'react';
import classNames from 'classnames/bind';
import { DateTime } from 'luxon';

import { FILES_SEARCH_ICONS_MAP } from 'files/services/icons_map';
import { FilesSearchWidget as FileSearchWidgetType } from 'types/dashboard_widget';
import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';

import { useDashboardFilterParams } from 'dashboards/hooks/useDashboardFilterParams';
import { isNotNullOrUndefined } from 'services/nullable';
import { formatPretty } from 'services/string';

import { useCameras } from 'cameras/hooks/useCameras';
import { useFilesSearch } from 'files/hooks/useFilesSearch';
import { useModal } from 'hooks/useModal';
import { useStreams } from 'streams/hooks/useStreams';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';

import { Button } from 'components/Button/Button';
import { EmptyView } from 'components/EmptyView/EmptyView';
import { Icon } from 'components/Icon/Icon';
import { Pagination } from 'components/Pagination/Pagination';
import { Pill } from 'components/Pill/Pill';
import { Popover } from 'components/Popover/Popover';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { Text } from 'components/Text/Text';
import { Thumbnail } from 'components/Thumbnail/Thumbnail';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import { FilesWidgetSkeleton } from './Skeleton';
import styles from './Files.module.scss';

const c = classNames.bind(styles);

export function FilesSearchWidget(widget: FileSearchWidgetType) {
  const { options } = widget;

  const { gateway_id, stream_ids = [] } = useDashboardFilterParams();

  const { start, stop, timezone } = useTimeRangeParams();

  const { open } = useModal();

  const [page, setPage] = React.useState(1);

  const gatewayIdsFilter = gateway_id
    ? Array.from(new Set([gateway_id, ...(options.gateway_ids || [])]))
    : options.gateway_ids;

  const streamIdsFilter = Array.from(
    new Set([...stream_ids, ...(options.stream_ids || [])])
  );

  const {
    data: files,
    isFetching,
    isPreviousData,
    isInitialLoading: isLoadingFiles,
    error,
  } = useFilesSearch({
    params: {
      created_ts_since: start,
      created_ts_until: stop,
      page,
      ...options,
      gateway_ids: gatewayIdsFilter,
      stream_ids: streamIdsFilter.length ? streamIdsFilter : undefined,
    },
    refetchInterval: WIDGET_REFETCH_INTERVAL,
    keepPreviousData: true,
  });

  React.useEffect(() => {
    if (page !== 1 && error && error.code === 'invalid-pagination-parameters') {
      setPage(1);
    }
  }, [error, page]);

  const streamIds = [
    ...new Set(
      files?.data.map((file) => file.stream_id).filter(isNotNullOrUndefined)
    ),
  ];

  const { data: streams, isInitialLoading: isLoadingStreams } = useStreams({
    params: {
      stream_ids: streamIds,
      limit: streamIds.length,
    },
    enabled: streamIds.length > 0,
  });

  const streamsCamerasMap = streams
    ? Object.fromEntries(
        streams?.data
          .filter(({ camera_id }) => camera_id)
          .map(({ id, camera_id }) => [id, camera_id])
      )
    : {};

  const cameraIds = [
    ...new Set(Object.values(streamsCamerasMap).filter(isNotNullOrUndefined)),
  ];

  const { data: cameras, isInitialLoading: isLoadingCameras } = useCameras({
    params: { camera_ids: cameraIds, limit: cameraIds.length },
    enabled: cameraIds.length > 0,
  });

  const isLoading = isLoadingFiles || isLoadingStreams || isLoadingCameras;

  const hasFiles = files && files.data.length > 0;
  const showLoading =
    isLoading || (isFetching && !hasFiles) || (isFetching && isPreviousData);

  return (
    <Widget widget={widget}>
      <ul className={c('files')}>
        {!hasFiles && !isLoading && !isFetching && (
          <EmptyView>No files for this time period and filters.</EmptyView>
        )}

        {showLoading ? (
          <SkeletonList
            min={hasFiles ? files?.data.length : 5}
            max={hasFiles ? files?.data.length : 5}
            component={<FilesWidgetSkeleton />}
          />
        ) : (
          <>
            {files?.data.map((file) => {
              const cameraId =
                file.stream_id && streamsCamerasMap[file.stream_id];
              const isFromCameraStream = Boolean(file.stream_id && cameraId);
              const source = isFromCameraStream
                ? cameras?.data.find(({ id }) => id === cameraId)
                : streams?.data.find(({ id }) => id === file.stream_id);

              const [firstEvent, secondEvent, ...moreEvents] = file.events;

              const objectsWithIcon = file.objects
                .filter((object) => FILES_SEARCH_ICONS_MAP[object])
                .slice(0, 6);

              const objectsWithoutIcon = file.objects.filter(
                (object) => !objectsWithIcon.includes(object)
              );

              return (
                <li key={file.file_id}>
                  <button
                    className={c('file', 'border')}
                    onClick={() =>
                      open('files-search', {
                        defaultFile: {
                          id: file.file_id,
                          created_at: file.created_at,
                        },
                        defaultStreamId: file.stream_id,
                      })
                    }
                  >
                    <Thumbnail
                      thumbnail_url={file.thumbnail_url}
                      mime_type={file.mime_type}
                    />
                    <div className={c('info')}>
                      <Text size="small" inline>
                        {DateTime.fromISO(file.created_at)
                          .setZone(timezone)
                          .toLocaleString(DateTime.DATETIME_MED)}
                      </Text>

                      <div className={c('entities')}>
                        {source && (
                          <Text className={c('text')} inline>
                            <Icon
                              name={isFromCameraStream ? 'camera' : 'stream'}
                            />
                            <span className={c('text-inner')}>
                              {source.name}
                            </span>
                          </Text>
                        )}
                      </div>

                      <div className={c('objects')}>
                        {objectsWithIcon.map((object) => (
                          <Tooltip key={object} content={object}>
                            <Icon name={FILES_SEARCH_ICONS_MAP[object]} />
                          </Tooltip>
                        ))}

                        {objectsWithoutIcon.length > 0 && (
                          <Tooltip content={objectsWithoutIcon.join(', ')}>
                            <span className={c('icon')}>
                              +{objectsWithoutIcon.length}
                            </span>
                          </Tooltip>
                        )}
                      </div>

                      {file.events.length ? (
                        <div className={c('events')}>
                          <Icon name="alarm" />
                          <Pill size="small">{formatPretty(firstEvent)}</Pill>
                          {secondEvent && (
                            <Pill size="small">
                              {formatPretty(secondEvent)}
                            </Pill>
                          )}
                          {moreEvents.length > 0 && (
                            <Popover
                              trigger={
                                <Button size="xsmall" variant="tertiary">
                                  +{moreEvents.length}
                                </Button>
                              }
                            >
                              <ul>
                                {moreEvents.map((event) => (
                                  <li key={event}>{formatPretty(event)}</li>
                                ))}
                              </ul>
                            </Popover>
                          )}
                        </div>
                      ) : null}

                      {file.texts.length ? (
                        <Text className={c('text')} inline>
                          <Icon name="clipboard" />
                          <Tooltip content={file.texts.join(', ')}>
                            <Text className={c('text-inner')}>
                              {formatPretty(file.texts.join(', '))}
                            </Text>
                          </Tooltip>
                        </Text>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </>
        )}

        {hasFiles && files.total_pages > 1 && (
          <Pagination
            className={c('pagination')}
            total={files.total_pages}
            value={page}
            onChange={setPage}
            siblings={0}
          />
        )}
      </ul>
    </Widget>
  );
}
